import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Label } from 'reactstrap';
import Switch from "react-switch";

const Offsymbol = () => <div style={{ display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, color: "#fff" }} />;
const OnSymbol = () => <div style={{ display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, color: "#fff" }} />;

const CreateTeam = ({ visible, solutions, zones, handleClose, initialData = '', onSubmit, shifts }) => {
  const [shiftOptions, setShiftOptions] = useState([]);

  useEffect(() => {
    if (shifts?.Data?.length > 0) {
      setShiftOptions(
        shifts?.Data?.map(shift => ({ value: shift?.shiftId, label: shift?.shiftName }))
      );
    }
  }, [shifts]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      teamName: initialData?.teamName || '',
      shiftId: initialData?.shiftId || '',
      breakTimeInMins: initialData?.breakTimeInMins ?? '',
      specialization: initialData?.specialization || '',
      isActive: initialData?.isActive || false,
      solutions: initialData?.solutionIds
        ? initialData?.solutionIds.toString().split(',')
        : [],
      zones: initialData?.zoneIds
        ? initialData?.zoneIds.toString().split(',')
        : []
    },
    validationSchema: Yup.object({
      teamName: Yup.string().required('Team Name is required'),
      shiftId: Yup.string().required('Shift is required'),
      breakTimeInMins: Yup.number()
        .required('Break Time is required')
        .integer('Break Time must be an integer')
        .min(0, 'Break Time must be positive or zero'),
      specialization: Yup.string().required('Specialization is required'),
      solutions: Yup.array().min(1, 'At least one solution is required'),
      zones: Yup.array().min(1, 'At least one zone is required')
    }),
    onSubmit: (values, { resetForm }) => {
      const payload = {
        sp: initialData?.teamId ? "usp_UpdateTeam" : "usp_InsertTeam",
        teamName: values?.teamName,
        shiftId: values?.shiftId,
        breakTimeInMins: values?.breakTimeInMins,
        specialization: values?.specialization,
        isActive: values?.isActive,
        solutionId: values?.solutions.join(","),
        zoneIds: values?.zones.join(",")
      };

      if (initialData?.teamId) {
        payload.teamId = initialData?.teamId;
      }
      onSubmit(payload, onClose);
    }
  });

  const onClose = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Modal
      title={initialData ? "Edit Team" : "Create New Team"}
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={650}
      className="custom-modal-header p-0"
      maskClosable={false}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="row g-3">

          {/* Team Name */}
          <div className="col-md-12">
            <label className="form-label fs-7">Team Name</label> <span className='text-danger'>*</span>
            <input
              type="text"
              className="form-control form-control-md form-control-solid fs-7 bg-body-secondary"
              placeholder="Enter team name"
              name="teamName"
              value={formik.values.teamName}
              onChange={formik.handleChange}
            />
            {formik.touched.teamName && formik.errors.teamName && (
              <span style={{ color: 'red' }}>{formik.errors.teamName}</span>
            )}
          </div>

          {/* Shift */}
          <div className="col-md-6">
            <label className="form-label fs-7">Shift</label> <span className='text-danger'>*</span>
            <Select
              options={shiftOptions}
              value={shiftOptions.find(opt => opt.value === formik.values.shiftId)}
              onChange={opt => formik.setFieldValue('shiftId', opt.value)}
              placeholder="Select a shift"
            />
            {formik.touched.shiftId && formik.errors.shiftId && (
              <span style={{ color: 'red' }}>{formik.errors.shiftId}</span>
            )}
          </div>

          {/* Solutions (multi select) */}
          <div className="col-md-6">
            <label className="form-label fs-7">Solutions</label> <span className='text-danger'>*</span>
            <Select
              isMulti
              options={solutions?.map(item => ({ label: item?.solutionName, value: item?.solutionId }))}
              value={formik.values.solutions.map(id => {
                const s = solutions?.find(sol => sol.solutionId == id);
                return { label: s?.solutionName, value: id };
              })}
              onChange={opts => formik.setFieldValue('solutions', opts.map(o => o.value))}
              placeholder="Select solutions"
            />
            {formik.touched.solutions && formik.errors.solutions && (
              <span style={{ color: 'red' }}>{formik.errors.solutions}</span>
            )}
          </div>

          {/* Zones (multi select) */}
          <div className="col-md-6">
            <label className="form-label fs-7">Zones</label> <span className='text-danger'>*</span>
            <Select
              isMulti
              options={zones?.Data?.map(item => ({ label: item?.zoneName, value: item?.zoneId }))}
              value={formik.values.zones.map(id => {
                const z = zones?.Data?.find(zone => zone.zoneId == id);
                return { label: z?.zoneName, value: id };
              })}
              onChange={opts => formik.setFieldValue('zones', opts.map(o => o.value))}
              placeholder="Select zones"
            />
            {formik.touched.zones && formik.errors.zones && (
              <span style={{ color: 'red' }}>{formik.errors.zones}</span>
            )}
          </div>

          {/* Break Time */}
          <div className="col-md-6">
            <label className="form-label fs-7">Break Time (mins)</label> <span className='text-danger'>*</span>
            <input
              type="number"
              className="form-control form-control-md form-control-solid fs-7 bg-body-secondary"
              name="breakTimeInMins"
              value={formik.values.breakTimeInMins}
              onChange={formik.handleChange}
              placeholder="Enter break time"
            />
            {formik.touched.breakTimeInMins && formik.errors.breakTimeInMins && (
              <span style={{ color: 'red' }}>{formik.errors.breakTimeInMins}</span>
            )}
          </div>

          {/* Specialization */}
          <div className="col-md-6">
            <label className="form-label fs-7">Specialization</label> <span className='text-danger'>*</span>
            <input
              type="text"
              className="form-control form-control-md form-control-solid fs-7 bg-body-secondary"
              placeholder="Enter specialization"
              name="specialization"
              value={formik.values.specialization}
              onChange={formik.handleChange}
            />
            {formik.touched.specialization && formik.errors.specialization && (
              <span style={{ color: 'red' }}>{formik.errors.specialization}</span>
            )}
          </div>

          {/* Active switch */}
          <div className="col-md-6">
            <div className="d-flex gap-2 flex-column align-items-start mt-3">
              <Label className='mb-0'>Is Active</Label> 
              <Switch
                uncheckedIcon={<Offsymbol />}
                checkedIcon={<OnSymbol />}
                onColor="#626ed4"
                onChange={() => formik.setFieldValue('isActive', !formik.values.isActive)}
                checked={formik.values.isActive}
                width={40}
                height={20}
                handleDiameter={18}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer mt-3">
          <button type="button" className="btn btn-light" onClick={onClose}>Close</button>
          <button type="submit" className="btn btn-primary ms-3">
            {initialData ? "Update" : "Add"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTeam;
